import { Component, Directive, forwardRef, HostListener, Injector, Input, Type, ViewContainerRef } from '@angular/core';
import { HasResult } from 'components/core/notification-popup/notification-popup.component';
import { AccessBlockedComponent } from 'components/core/workspace/access-blocked.component';
import { DraggableDirective } from 'directives/draggable.directive';
import { TranslatePipe } from 'pipes/translate.pipe';
import { CredentialService } from 'services/credential.service';
import { InputContainerService } from 'services/input-container.service';
import { IViewOptions, NavigationService, ViewType } from 'services/navigation.service';
import { WorkspaceService } from 'services/workspace.service';

export class WorkspaceView {
	relativeViews: Map<string, WorkspaceView> = new Map<string, WorkspaceView>();
	title = '';
	component?: Object;

	constructor(public type: Type<Object>, public resolve: Function) { }
}

@Directive({ selector: '[component-loader]' })
export class ComponentLoader {

	@Input()
	set view(view: WorkspaceView | undefined) {
		if (view) {
			// Load the component indicated by the view's type, and resolve 
			// the view with the component instance, to inform that the DOM has been loaded
			this.loadComponent(view);
		}
	}

	constructor(private containerReference: ViewContainerRef, private readonly injector: Injector) { }

	private loadComponent(view: WorkspaceView) {
		this.containerReference.clear();
		const injector = Injector.create({
			providers: [{ provide: WorkspaceView, useValue: view }],
			parent: this.injector
		})
		view.resolve(view.component = this.containerReference.createComponent(view.type, { injector: injector }).instance);
	}
}

@Component({
	selector: 'workspace',
	templateUrl: './workspace.component.html',
	styleUrls: ['./workspace.component.scss'],
	providers: [WorkspaceService],
	imports: [ComponentLoader, forwardRef(() => PopupPanelComponent)]
})
export class WorkspaceComponent {

	public activeViews: WorkspaceView[] = [];
	public popupViews: WorkspaceView[] = [];
	public navigationView?: WorkspaceView;
	private viewReferences = new Map<Object, WorkspaceView>();

	constructor(private navigation: NavigationService, private credentialService: CredentialService) {
		this.navigation.registerViewLoader(
			<T extends Object>(t: Type<T>, o: IViewOptions): Promise<T> => { return this.loadComponent(t, o); },
			(o: Object): void => { this.removeComponent(o); });
	}

	identifyView(view: WorkspaceView) { return view; } // Hides warnings from Angular where the entire activeViews array is replaced by a new view, and it thinks that wasn't the intent

	@HostListener('document:keydown.escape')
	closePopup(identifier?: number | WorkspaceView) {
		let index: number = this.popupViews.length - 1;
		if (identifier instanceof WorkspaceView) {
			index = this.popupViews.indexOf(identifier);
			if (index < 0) return;
		}
		else if (identifier !== undefined) {
			index = identifier;
		}
		const closedViews = this.popupViews.splice(index, 1);
		const closedComponent = closedViews[0]?.component;
		(closedComponent as HasResult)?.result?.emit(false); // If closing a component with a "result" eventemitter, tell any listeners the result is false
	}

	private removeComponent(component: Object, replaceView?: WorkspaceView): void {
		let view = this.viewReferences.get(component);
		if (view == undefined) return;
		this.cleanupWorkspaceViews(view);
		if (this.activeViews.some(activeView => activeView === view)) {
			const index = this.activeViews.findIndex(activeView => activeView === view);
			this.activeViews.splice(index, 1);
			if (replaceView) this.activeViews.splice(index, 0, replaceView);
		}
		if (this.popupViews.some(popupView => popupView === view)) {
			const index = this.popupViews.findIndex(popupView => popupView === view);
			this.popupViews.splice(index, 1);
			if (replaceView) this.popupViews.splice(index, 0, replaceView);
		}
	}

	private loadComponent<T extends Object>(componentType: Type<T>, options: IViewOptions): Promise<T> {
		let newView: WorkspaceView;

		return new Promise<T>((resolve, reject) => {

			const components = this.credentialService.filterAccessibleComponents([componentType]);

			if (components.length == 0) {
				newView = new WorkspaceView(AccessBlockedComponent, resolve);
			} else {
				newView = new WorkspaceView(componentType, resolve);
			}
			newView.title = options.title ?? '';

			if (options.viewType == ViewType.Full) {

				// View type Full = replace all open views with the new view

				this.navigationView = undefined;
				this.activeViews = [];
				this.activeViews.push(newView);
			}
			if (options.viewType == ViewType.Relative && options.referenceId && options.referenceContainer) {

				// View type Relative = new view is opened next to referenced container.
				// Reference ID means that a new view opened with the same ID will replace that view

				let parentView = this.viewReferences.get(options.referenceContainer);
				if (!parentView) throw new Error('Parent view not available');

				let relative = parentView.relativeViews.get(options.referenceId);
				if (relative && !options.avoidReplace) {
					this.cleanupWorkspaceViews(relative);

					// Replace the existing relative view
					this.activeViews.splice(this.activeViews.indexOf(relative), 1, newView);
				} else if (!relative) {
					// Insert new view after the parent view
					this.activeViews.splice(this.activeViews.indexOf(parentView) + 1, 0, newView);
				}
				parentView.relativeViews.set(options.referenceId, newView);
			}
			if (options.viewType == ViewType.Parent && options.referenceId && options.referenceContainer) {

				const childView = this.viewReferences.get(options.referenceContainer);
				if (!childView || this.childRelative(childView)) return;

				this.activeViews.splice(this.activeViews.indexOf(childView), 0, newView);
				newView.relativeViews.set(options.referenceId, childView);
			}
			if (options.viewType == ViewType.Navigation) {

				if (options.referenceId && options.referenceContainer) {
					const childView = this.viewReferences.get(options.referenceContainer);
					if (childView && this.childRelative(childView)) return;
					if (childView) newView.relativeViews.set(options.referenceId, childView);
				}

				this.navigationView = newView;
			}
			if (options.viewType == ViewType.Same && options.referenceContainer) {

				// View type Same = new view replaces existing view

				this.removeComponent(options.referenceContainer, newView);
			}
			if (options.viewType == ViewType.New) {

				// View type New = new view is appended to the end of currently active views

				this.activeViews.push(newView);
			}
			if (options.viewType == ViewType.Popup) {

				// View type Popup opens in a special container for this purpose, only one can exist
				this.popupViews.push(newView);
			}

		}).then((component) => {
			// When the view resolves with a loaded component, save the reference to the view from that component.
			this.viewReferences.set(component, newView);
			return component;
		});
	}

	private childRelative(child: WorkspaceView, views?: WorkspaceView[]): boolean {

		if (!views) views = this.activeViews.concat(this.navigationView ? [this.navigationView] : []);
		let isRelative = false;

		for (let view of views) {
			const relativeViews = Array.from(view.relativeViews.values());
			if (relativeViews.find(relativeView => relativeView.type == child.type)) {
				isRelative = true;
				break;
			}
			this.childRelative(child, relativeViews);
		}
		return isRelative;
	}

	private cleanupWorkspaceViews(workspace: WorkspaceView) {

		workspace.relativeViews.forEach(relativeView => {
			this.cleanupWorkspaceViews(relativeView);
			this.activeViews.splice(this.activeViews.findIndex(activeView => activeView == relativeView), 1);
		});
	}
}

@Component({
	selector: 'popup-panel',
	templateUrl: './popup-panel.component.html',
	providers: [InputContainerService],
	imports: [DraggableDirective, ComponentLoader, TranslatePipe]
})
export class PopupPanelComponent {
	@Input() view!: WorkspaceView;

	close() {
		this.workspace.closePopup(this.view);
	}
	constructor(private inputContainer: InputContainerService, private workspace: WorkspaceComponent) {
		setTimeout(() => {
			this.inputContainer.focus();
		});
	}
}
